/**
 * @file: useTypingIndicator.ts
 *
 * The RECEIVING side of "X 正在输入…": the set of peers currently typing,
 * exposed as a plain observable of their names. It owns ONLY typing
 * information — no title, no navigation, no member count. A consumer (the
 * desktop pane header, the mobile navigator-title driver) subscribes and folds
 * the names into whatever UI it owns.
 *
 * The whole thing is one RxJS pipeline (no `setState`/`setInterval` in a
 * component body): live room frames + a 1s tick are merged and `scan`ned into
 * a per-sender map with a 5s TTL, so a started typist auto-expires if no
 * "stopped" arrives, and a sender's own new message clears them immediately.
 * `distinctUntilChanged` keeps the tick from re-emitting an unchanged set, and
 * `shareReplay(1)` lets late/multiple subscribers share one WS subscription.
 */

import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { useMemo } from 'react';
import { interval, merge, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
} from 'rxjs/operators';
import { useChatConversation } from '../context';
import { isBaked } from '../handle';

/** A started typist is shown for this long unless refreshed / stopped. */
const TYPING_TTL_MS = 5000;
const TICK_MS = 1000;

type TypingSignal =
  | { kind: 'start'; id: string; name: string }
  | { kind: 'stop'; id: string }
  | { kind: 'tick' };

interface TypingEntry {
  name: string;
  expiresAt: number;
}

export function useTypingIndicator(): Observable<string[]> {
  const { conversationHandle } = useChatConversation();
  const { wsClient } = useChatSession();
  const roomId = isBaked(conversationHandle) ? conversationHandle.roomId : null;

  return useMemo(() => {
    if (!roomId) return of<string[]>([]);

    // The server excludes the typist from the broadcast, so a `typing` frame
    // is always from someone else; a new message from a sender implicitly
    // ends their typing.
    const signals$ = wsClient.onRoomEvent(roomId).pipe(
      map((event): TypingSignal | null => {
        if (event.kind === 'typing') {
          const id = String(event.platformUserId);
          return event.body.stopped === true
            ? { kind: 'stop', id }
            : { kind: 'start', id, name: event.authorName };
        }
        if (event.kind === 'message.new' && event.type === 'user') {
          return { kind: 'stop', id: String(event.platformUserId) };
        }
        return null;
      }),
      filter((s): s is TypingSignal => s !== null),
    );
    const tick$ = interval(TICK_MS).pipe(
      map((): TypingSignal => ({ kind: 'tick' })),
    );

    return merge(signals$, tick$).pipe(
      scan((entries, signal) => {
        const now = Date.now();
        const next = new Map(
          Array.from(entries).filter(([, v]) => v.expiresAt > now),
        );
        if (signal.kind === 'start') {
          next.set(signal.id, {
            name: signal.name,
            expiresAt: now + TYPING_TTL_MS,
          });
        } else if (signal.kind === 'stop') {
          next.delete(signal.id);
        }
        return next;
      }, new Map<string, TypingEntry>()),
      map(entries =>
        Array.from(entries.values())
          .map(e => e.name)
          .filter(Boolean),
      ),
      distinctUntilChanged(
        (a, b) => a.length === b.length && a.every((n, i) => n === b[i]),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }, [roomId, wsClient]);
}
